import Button from '@rio-cloud/rio-uikit/Button';
import { FunctionComponent } from 'react';
import { assertExhaustive } from '../../../../../utils';
import { NoValue } from '../../../sharedComponents/NoValue';
import { DepartedVehicle } from '../../compoundManagement.types';
import {
    DisplayArrivalDate,
    DisplayArrivalFreightForwarder,
    DisplayArrivalFromCity,
    DisplayArrivalFromLocationId,
} from '../../shared/dataDisplay/ArrivalDataContent';
import {
    DisplayDepartureDate,
    DisplayDepartureFreightForwarder,
    DisplayDepartureToCity,
    DisplayDepartureToLocationId,
} from '../../shared/dataDisplay/DepartureDataContent';
import { DisplayDispatchType } from '../../shared/dataDisplay/DisplayDispatchType';
import { DisplayFuelType } from '../../shared/dataDisplay/DisplayFuelType';
import { DisplayModelName } from '../../shared/dataDisplay/DisplayModelName';
import { CompoundVehicleActionButton } from '../../shared/table/CompoundVehicleActionButton';
import { DepartedVehiclesTableColumn } from './DepartedVehiclesTableColumn';

type TableDataContentProps = {
    column: DepartedVehiclesTableColumn;
    vehicle: DepartedVehicle;
};

export const DepartedVehicleTableDataContent: FunctionComponent<TableDataContentProps> = ({ vehicle, column }) => {
    const hasArrival = vehicle.arrival !== undefined;
    const hideActionButton =
        (hasArrival && (vehicle.arrival.isCancelled || vehicle.arrival.isFinalized) && vehicle.departure.isFinalized) ||
        (!hasArrival && (vehicle.departure.isFinalized || vehicle.departure.isCancelled));

    switch (column) {
        case DepartedVehiclesTableColumn.VIN:
            return <div className="ellipsis-1">{vehicle.vehicle.vin}</div>;
        case DepartedVehiclesTableColumn.LOAD_ID:
            return spanOrNoValue(vehicle.departure.transportInformation.loadId);
        case DepartedVehiclesTableColumn.LOCATION_ID:
            return <span>{vehicle.compoundLocation.identifier}</span>;
        case DepartedVehiclesTableColumn.LOCATION_CITY:
            return <span>{vehicle.compoundLocation.address.city}</span>;
        case DepartedVehiclesTableColumn.MANUFACTURER:
            return spanOrNoValue(vehicle.vehicle.model.manufacturer);
        case DepartedVehiclesTableColumn.MODEL:
            return <DisplayModelName vehicle={vehicle.vehicle} />;
        case DepartedVehiclesTableColumn.DISPATCH_TYPE:
            return <DisplayDispatchType vehicle={vehicle.vehicle} />;
        case DepartedVehiclesTableColumn.FUEL_TYPE:
            return <DisplayFuelType vehicle={vehicle.vehicle} />;
        case DepartedVehiclesTableColumn.MODEL_VERSION:
            return spanOrNoValue(vehicle.vehicle.model.version);
        case DepartedVehiclesTableColumn.ARRIVAL_DATE:
            return <DisplayArrivalDate compoundVehicle={vehicle} />;
        case DepartedVehiclesTableColumn.ARRIVAL_FROM_LOCATION_ID:
            return <DisplayArrivalFromLocationId compoundVehicle={vehicle} />;
        case DepartedVehiclesTableColumn.ARRIVAL_FROM_CITY:
            return <DisplayArrivalFromCity compoundVehicle={vehicle} />;
        case DepartedVehiclesTableColumn.ARRIVAL_FREIGHT_FORWARDER:
            return <DisplayArrivalFreightForwarder arrival={vehicle.arrival} />;
        case DepartedVehiclesTableColumn.DEPARTURE_DATE:
            return <DisplayDepartureDate compoundVehicle={vehicle} />;
        case DepartedVehiclesTableColumn.DEPARTURE_TO_LOCATION_ID:
            return <DisplayDepartureToLocationId compoundVehicle={vehicle} />;
        case DepartedVehiclesTableColumn.DEPARTURE_TO_CITY:
            return <DisplayDepartureToCity compoundVehicle={vehicle} />;
        case DepartedVehiclesTableColumn.DEPARTURE_FREIGHT_FORWARDER:
            return <DisplayDepartureFreightForwarder departure={vehicle.departure} />;
        case DepartedVehiclesTableColumn.ACTION:
            return (
                hideActionButton || (
                    <CompoundVehicleActionButton
                        labelId="outboundPortal.compoundManagement.button.updateArrivalOrDeparture"
                        icon="rioglyph-refresh"
                        vehicle={vehicle}
                        buttonStyle={Button.DEFAULT}
                    />
                )
            );
        default:
            assertExhaustive(column);
    }
};

const spanOrNoValue = (text: string | undefined) => (text ? <span>{text}</span> : <NoValue />);
